import { Card, CardContent } from '@/components/ui/card';
import { LocationStatusBadge } from '../shared/StatusBadge';
import type { Location, BinType } from '../../types';

const binTypeLabels: Record<BinType, string> = {
  standard: 'Standard',
  bulk: 'Bulk',
  calibration: 'Calibration',
  staging: 'Staging',
  returns: 'Returns',
  overflow: 'Overflow',
};

interface BinCardProps {
  location: Location;
  onClick?: () => void;
}

export function BinCard({ location, onClick }: BinCardProps) {
  const isActive = location.status === 'active';

  return (
    <Card
      className={`transition-all hover:shadow-md ${
        !isActive ? 'opacity-60' : ''
      }`}
    >
      <CardContent className="p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            {location.zone_color && (
              <div
                className="w-2.5 h-2.5 rounded-full shrink-0"
                style={{ backgroundColor: location.zone_color }}
              />
            )}
            <span className="font-mono font-semibold text-sm">
              {location.full_code || `${location.zone_code}-${location.bin_code}`}
            </span>
          </div>
          <LocationStatusBadge status={location.status} />
        </div>
        {location.name && (
          <p className="text-xs text-muted-foreground mb-2 truncate">{location.name}</p>
        )}
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>{binTypeLabels[location.bin_type]}</span>
          <button
            type="button"
            onClick={onClick}
            className="text-atek-blue hover:underline text-xs cursor-pointer"
          >
            Edit
          </button>
        </div>
      </CardContent>
    </Card>
  );
}

interface BinGridProps {
  locations: Location[];
  onLocationClick?: (location: Location) => void;
}

export function BinGrid({ locations, onLocationClick }: BinGridProps) {
  if (locations.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No bins found. Add bins to this zone to get started.
      </div>
    );
  }

  return (
    <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
      {locations.map((location) => (
        <BinCard
          key={location.id}
          location={location}
          onClick={() => onLocationClick?.(location)}
        />
      ))}
    </div>
  );
}
